"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SubFormLinkService = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const field_transform_1 = require("./field.transform");
const log_service_1 = require("../log.service");
class SubFormLinkService {
    constructor() {
        this.__priority = 100;
        this.log = log_service_1.Logger;
    }
    isSubFormCompatible(form) {
        return this.getRules(form) != null || form.subFormType === core_api_1.SubFormType.SPAN;
    }
    findLinks(collection, candidateCollections) {
        const subFormLinks = [];
        const location = collection.location;
        const fields = collection.schema.fields.slice();
        while (fields.length > 0) {
            const fieldSchema = fields.pop();
            if (!fieldSchema) {
                continue;
            }
            if (fieldSchema.type !== core_api_1.IkeFieldType.FORM) {
                continue;
            }
            const subform = collection.getField(fieldSchema.id);
            const rules = this.getRules(subform.schema);
            if (rules == null) {
                // FIXME: This seems wrong -- it adds instance IDs to list of field IDs, so nested subforms are never actually searched
                fields.push.apply(fields, subform.value);
                continue;
            }
            for (const candidateCollection of candidateCollections) {
                const subFormLink = this.getLink(rules, subform, location, candidateCollection);
                if (subFormLink !== null) {
                    subFormLinks.push(subFormLink);
                    continue;
                }
            }
        }
        return subFormLinks;
    }
    /**
     * Legacy method for pull-flow span linking.
     * @deprecated Use copyInstance() directly
     */
    copySubForm(collection, subFormLinkRecord) {
        const targetSubForm = collection.getField(subFormLinkRecord.instance.schema.id);
        return this.copyInstance({
            sourceInstance: subFormLinkRecord.instance,
            targetSubForm,
            sourceCollection: subFormLinkRecord.collection,
        });
    }
    /**
     * Copy a subform instance to a target subform, applying field transformations.
     * Supports both creating a new instance and updating an existing one.
     *
     * Field transformations applied:
     * - Bearing inversion (180 degree flip)
     * - Span type replacement (NextPole to PreviousPole, OtherPole unchanged)
     * - Attachment height exclusions (cleared, not copied)
     *
     * @param options Configuration for the copy operation
     * @returns The target instance (created or existing)
     * @throws Error if targetInstanceId is provided but not found
     */
    copyInstance(options) {
        const { sourceInstance, targetSubForm, targetInstanceId, sourceCollection } = options;
        // Get or create target instance
        let targetInstance;
        if (targetInstanceId) {
            // Update existing instance
            const existing = targetSubForm.getInstance(targetInstanceId);
            if (!existing) {
                throw new Error(`Target instance ${targetInstanceId} not found`);
            }
            targetInstance = existing;
        }
        else {
            // Always create a new instance with a fresh ID. Using the source's ID
            // would collide with an existing span if the target was duplicated from
            // the source or previously linked to it.
            const newInstanceId = targetSubForm.Mutator.create();
            const created = targetSubForm.getInstance(newInstanceId);
            if (!created) {
                throw new Error('Failed to create target instance');
            }
            targetInstance = created;
        }
        // Save height measurements from target before overwriting (update existing flow only).
        // The copy pipeline clears pole-specific heights (attachment, bolt, etc.) since they
        // shouldn't carry over from the source. But if the target already had measurements
        // taken on its own photos, we want to restore those after the copy.
        //
        // After saving, we null out the saved fields' state values. This is critical:
        // ModelMutatorSubForm.copy() calls clear() which calls manual(null) on each field.
        // For photomeasure fields, manual(null) calls unlink() which calls label.unlink(fieldId).
        // If that was the label's last link, label.remove() destroys BOTH the label AND its
        // parent marker drawable. By nulling the state first, isPhotoMeasure() returns false
        // during clear(), unlink() is skipped, and the drawables survive.
        const savedHeightStates = new Map();
        if (targetInstanceId) {
            targetInstance.forEachField((field) => {
                if (core_shared_1.CollectionModelFieldHeight.isHeightField(field) &&
                    (field.isPhotoMeasure() || field.isPhotoMeasureManual())) {
                    const state = field.stateValue;
                    if ((state === null || state === void 0 ? void 0 : state.drawable) && (state === null || state === void 0 ? void 0 : state.captureId)) {
                        savedHeightStates.set(field.id, Object.assign({}, state));
                        targetInstance.collection.state.setFieldValue(field.id, null);
                    }
                }
            });
        }
        // Copy all field values from source to target
        this.copyFields(sourceInstance, targetInstance);
        // Apply transformation rules (bearing inversion, span type replacement, etc.)
        const rules = this.getRules(targetSubForm.schema);
        this.applyFieldTransforms(targetInstance, rules);
        // Clean up external capture references if sourceCollection provided
        if (sourceCollection) {
            this.cleanupExternalCaptureReferences(sourceCollection, targetInstance);
        }
        // Restore height measurements that were cleared by the pipeline but belong
        // to the target collection's own captures (not the source's).
        for (const [fieldId, savedState] of savedHeightStates) {
            if (targetInstance.collection.state.getFieldValue(fieldId) != null) {
                continue; // Field survived the pipeline — don't overwrite
            }
            try {
                const drawableState = targetInstance.collection.getDrawableState(savedState.captureId);
                if (!drawableState.hasDrawable(savedState.drawable)) {
                    continue; // Drawable was removed
                }
                // Restore state value
                targetInstance.collection.state.setFieldValue(fieldId, savedState);
                // Re-link the drawable's label to the field if not already linked.
                // The label may still have the link from before (we nulled the state
                // but didn't remove the label link), so check to avoid duplicates.
                const drawable = drawableState.getDrawable(savedState.drawable);
                const alreadyLinked = drawable.label.links.some((link) => link.id === fieldId);
                if (!alreadyLinked) {
                    targetInstance.forEachField((field) => {
                        if (field.id === fieldId && core_shared_1.CollectionModelFieldHeight.isHeightField(field)) {
                            drawable.label.linkHeight(field);
                            return false;
                        }
                        return true;
                    });
                }
            }
            catch (_a) {
                // Drawable or capture no longer exists — skip
            }
        }
        return targetInstance;
    }
    /**
     * Perform a complete two-way span link operation:
     * 1. Copy span data from source to target with transformations
     * 2. Set bidirectional vector links between collections
     * 3. Clean up external capture references
     *
     * @param options Configuration for the two-way link
     * @returns Result containing both collections and span instances
     * @throws Error if target collection has no span subform
     */
    performTwoWaySpanLink(options) {
        const { sourceSpanSubForm, sourceCollection, targetCollection, targetInstanceId } = options;
        // Get target span field
        const targetSpanField = targetCollection.spanSubForm;
        if (!targetSpanField) {
            throw new Error('Target collection has no span subform');
        }
        // Copy span data with transforms and capture cleanup
        const targetSpanInstance = this.copyInstance({
            sourceInstance: sourceSpanSubForm.instance,
            targetSubForm: targetSpanField,
            targetInstanceId,
            sourceCollection,
        });
        // Set up bidirectional vector links
        // Source → Target
        sourceSpanSubForm.spanVectorField.Mutator.collection(targetCollection.id);
        // Target → Source
        const targetSpanAdaptor = new core_shared_1.SpanSubFormAdaptor(targetSpanInstance);
        targetSpanAdaptor.spanVectorField.Mutator.collection(sourceCollection.id);
        return {
            sourceCollection,
            targetCollection,
            sourceSpanInstance: sourceSpanSubForm.instance,
            targetSpanInstance,
        };
    }
    /**
     * Find the reciprocal span link on the target collection.
     * Given a span vector field that links to another collection, checks whether
     * the target collection has a span that links back to the source.
     *
     * @returns The reciprocal link info, or null if no two-way link exists
     */
    findReciprocalLink(field, sourceCollection) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const targetCollection = yield field.getLinkedCollection();
            if (!targetCollection) {
                return null;
            }
            const targetSpanSubForm = targetCollection.spanSubForm;
            if (!targetSpanSubForm) {
                return null;
            }
            for (const instance of targetSpanSubForm.value) {
                try {
                    const adaptor = new core_shared_1.SpanSubFormAdaptor(instance);
                    if (adaptor.spanTargetCollectionId === sourceCollection.id) {
                        return {
                            targetCollection,
                            targetSpanInstance: instance,
                            targetVectorField: adaptor.spanVectorField,
                        };
                    }
                }
                catch (_a) {
                    // Instance is not a valid SPAN subform - skip
                    continue;
                }
            }
            return null;
        });
    }
    /**
     * Search for SubForm instances on the candidateCollection which pass the given rules.
     * The candidateCollection must also be within the rule's distance of the given location.
     * @param rules Defines max distance and the vector field used to search for a linkable vector end point.
     * @param subform The subForm instance which we are searching from - the "end" of the link.
     * @param location The location of the collection that the subform instance (end point) exists on.
     * @param candidateCollection The collection we are searching for a vector field on whose end point is within range of the location.
     * @returns SubFormLink describing the link.
     */
    getLink(rules, subform, location, candidateCollection) {
        var _a;
        const candidateSubForm = candidateCollection.getField(subform.schema.id);
        if (candidateSubForm == null || !candidateSubForm.hasValue()) {
            return null;
        }
        const candidateLocation = candidateCollection.location;
        const subFormLinks = [];
        for (const candidateInstance of candidateSubForm.value) {
            // Skip if the instance ID of the candidate is the same as the current subform instance.
            // NOTE: This might not be desired behavior in some situations, since instance IDs are preserved when duplicating collections.
            if (subform.getInstance(candidateInstance.instanceId) != null) {
                this.log.warn({ collection: candidateCollection.id, instance: candidateInstance.instanceId }, `Skipping SubForm link candidate because it had the same instanceId: ${candidateCollection.title} (${candidateInstance.instanceId})`);
                continue;
            }
            // Check if any rules reject any field in the candidate instance
            if (!this.matchesRules(rules, candidateInstance)) {
                this.log.info({ collection: candidateCollection.id, instance: candidateInstance.instanceId, rules }, `Skipping SubForm link candidate because it failed rules check: ${candidateCollection.title} (${candidateInstance.instanceId})`);
                continue;
            }
            const vectorField = candidateInstance.getField(rules.vector);
            const distance = this.getDistance(candidateLocation, vectorField, location);
            if (distance == null || distance >= rules.distance) {
                this.log.info({ collection: candidateCollection.id, instance: candidateInstance.instanceId, rules, distance }, `Skipping SubForm link candidate because the endpoint was too far away: ${candidateCollection.title} (${candidateInstance.instanceId})`);
                continue;
            }
            subFormLinks.push({
                collection: candidateCollection,
                instance: candidateInstance,
                distance,
            });
        }
        subFormLinks.sort(this.sortSubFormLinkRecords);
        return (_a = subFormLinks[0]) !== null && _a !== void 0 ? _a : null;
    }
    matchesRules(rules, instance) {
        return instance.forEachField((field) => {
            const fieldRule = rules.fields[field.schema.id];
            if (!fieldRule) {
                return true;
            }
            const transform = field_transform_1.FieldTransform.create(field, fieldRule);
            if (transform == null) {
                return true;
            }
            const match = transform.match(field);
            if (!match) {
                this.log.info(`Rule match failed: ${transform.constructor.name} (field=${field.schema.id}, instance=${instance.instanceId})`);
            }
            return match;
        });
    }
    getDistance(sourceLocation, sourceVectorField, targetLocation) {
        let spanLocation1;
        let spanLocation2;
        if (sourceVectorField.provider === core_shared_1.CollectionModelFieldVector.PROVIDER_P2P) {
            const captures = sourceVectorField.getCaptures();
            spanLocation1 = captures[0].target;
            spanLocation2 = captures[1].target;
        }
        else if (sourceVectorField.provider === core_shared_1.CollectionModelFieldVector.PROVIDER_MANUAL ||
            sourceVectorField.provider === core_shared_1.CollectionModelFieldVector.PROVIDER_COLLECTION) {
            spanLocation1 = core_shared_1.IkeGeodesic.projectLocation(sourceLocation, sourceVectorField.value.bearing, sourceVectorField.value.distance);
            spanLocation2 = sourceLocation != null ? sourceLocation : spanLocation1;
        }
        else {
            return null;
        }
        const distance1 = core_shared_1.IkeGeodesic.getDistance(targetLocation, spanLocation1);
        const distance2 = core_shared_1.IkeGeodesic.getDistance(targetLocation, spanLocation2);
        return Math.min(distance1, distance2);
    }
    /**
     * Gets all capture ids for the collection ignoring the provided subform
     * @param collection The collection model
     * @param subformInstance The instance of the subform which you want the captures removed
     * @returns An array of captures which exist on the collection, but not the provided subform instance
     */
    getCaptureIdsExceptForSubForm(collection, subformInstance) {
        const captureIds = {};
        collection.forEachField((field) => {
            if (core_shared_1.CaptureProviderHelpers.isCaptureProvider(field)) {
                for (const capture of field.getCaptures()) {
                    captureIds[capture.objectId] = true;
                }
            }
        });
        subformInstance.forEachField((field) => {
            if (core_shared_1.CaptureProviderHelpers.isCaptureProvider(field)) {
                for (const capture of field.getCaptures()) {
                    delete captureIds[capture.objectId];
                }
            }
        });
        return captureIds;
    }
    /**
     * Remove height field references to captures that exist on sourceCollection
     * but not on the target collection (where targetInstance lives).
     */
    cleanupExternalCaptureReferences(sourceCollection, targetInstance) {
        const externalCaptureIds = this.getCaptureIdsExceptForSubForm(sourceCollection, targetInstance);
        targetInstance.forEachField((field) => {
            if (!core_shared_1.CollectionModelFieldHeight.isHeightField(field) || !field.isPhotoMeasure()) {
                return;
            }
            const stateValue = field.stateValue;
            if (!(stateValue === null || stateValue === void 0 ? void 0 : stateValue.captureId) || !(stateValue === null || stateValue === void 0 ? void 0 : stateValue.drawable)) {
                return;
            }
            const drawableState = targetInstance.collection.getDrawableState(stateValue.captureId);
            const drawable = drawableState.getDrawable(stateValue.drawable);
            if (externalCaptureIds[drawable.captureId]) {
                field.Mutator.unlink();
            }
        });
    }
    sortSubFormLinkRecords(record1, record2) {
        return record1.distance - record2.distance;
    }
    /**
     * Copy all field values from source instance to target instance.
     * Overwrites existing values on target (if source has a value).
     */
    copyFields(sourceInstance, targetInstance) {
        sourceInstance.forEachField((sourceField) => {
            const targetField = targetInstance.getOptionalField(sourceField.schema.id);
            if (!targetField) {
                return;
            }
            if (sourceField.hasValue()) {
                targetField.Mutator.copy(sourceField);
            }
            else {
                targetField.Mutator.clear();
            }
        });
    }
    /**
     * Apply transformation rules to all fields in an instance.
     */
    applyFieldTransforms(instance, rules) {
        if (!rules) {
            return;
        }
        instance.forEachField((field) => {
            const fieldRule = rules.fields[field.schema.id];
            if (!fieldRule) {
                return;
            }
            const transform = field_transform_1.FieldTransform.create(field, fieldRule);
            if (transform === null) {
                return;
            }
            transform.apply(field);
        });
    }
    getRules(form) {
        var _a;
        if (form.subFormType === core_api_1.SubFormType.SPAN) {
            const spanSubForm = (0, core_shared_1.readSpanForm)(form.getForm());
            if (!spanSubForm)
                return null;
            return new core_shared_1.SpanSubFormCopyProvider(spanSubForm);
        }
        for (const provider of (_a = form.providers) !== null && _a !== void 0 ? _a : []) {
            if (provider == null) {
                continue;
            }
            if (typeof provider === 'object') {
                if (provider.type === core_api_1.IkeProviderType.EXISTING_SPAN) {
                    return provider;
                }
            }
            else if (provider === core_api_1.IkeProviderType.EXISTING_SPAN) {
                return core_shared_1.IkeCalcExistingSpanLegacyProvider;
            }
        }
        return null;
    }
}
exports.SubFormLinkService = SubFormLinkService;
//# sourceMappingURL=subform.link.service.js.map